import { ChatMessage } from '../../features/chat/hooks/types';

export class ChatMessageMapper {
    static toDomain(row: any): ChatMessage {
        const message: ChatMessage = {
            id: row.message_id,
            type: row.message_type as any,
            text: row.content,
            sender: row.sender as any,
            timestamp: new Date(row.timestamp),
            status: row.status as any,
            media: row.media_url ? {
                url: row.media_url,
                transcription: row.transcription || null // Transcrição de áudio
            } : undefined,
            reactions: row.reactions || [],
            is_edited: row.is_edited || false
        };

        // Tenta obter quotedMessage de todas as fontes possíveis (JSON ou Legado)
        let quotedData: any = null;

        // 1. Tenta do JSON (novo padrão)
        if (row.quoted_message) {
            try {
                quotedData = typeof row.quoted_message === 'string'
                    ? JSON.parse(row.quoted_message)
                    : row.quoted_message;
            } catch (e) {
                console.warn('Erro parse quoted_message:', e);
            }
        }

        // 2. Se não achou válido no JSON, tenta colunas legadas
        if (!quotedData || !quotedData.id) {
            if (row.quoted_message_id) {
                quotedData = {
                    id: row.quoted_message_id,
                    content: row.quoted_content,
                    type: row.quoted_type || 'text',
                    sender: row.quoted_sender || 'them',
                    mediaUrl: row.quoted_media_url
                };
            }
        }

        if (quotedData && quotedData.id) {
            const mediaUrl = quotedData.mediaUrl || row.quoted_media_url || undefined;
            const rawType = quotedData.type || row.quoted_type || undefined;
            // Select legado sem quoted_type vinha como 'text' mesmo com mídia (ex.: reply de Story).
            const quotedType = (() => {
                if (rawType && rawType !== 'text') return rawType;
                if (!mediaUrl) return rawType || 'text';
                return /\.(mp4|mov|m4v|webm)(\?|$)/i.test(mediaUrl) ? 'video' : 'image';
            })();
            message.quotedMessage = {
                id: quotedData.id,
                content: quotedData.content ?? row.quoted_content ?? undefined,
                type: quotedType,
                sender: (quotedData.sender || row.quoted_sender) === 'me' ? 'me' : 'them',
                mediaUrl,
            };
        }

        // Mapeia ad_metadata (CTWA) se presente
        if (row.ad_metadata) {
            const ad = typeof row.ad_metadata === 'string'
                ? JSON.parse(row.ad_metadata)
                : row.ad_metadata;
            
            let imageUrl = ad.image_url;

            // Recupera imagem de Buffer serializado como objeto numérico ({"0":255,"1":216,...})
            // Este é o formato que o Supabase salva quando o jpegThumbnail vem como Buffer da Evolution API
            imageUrl = ChatMessageMapper.resolveImageUrl(imageUrl);

            message.adMetadata = {
                imageUrl: imageUrl || undefined,
                headline: ad.headline || undefined,
                body: ad.body || undefined,
                sourceUrl: ad.source_url || undefined,
                sourceId: ad.source_id || undefined,
                mediaType: ad.media_type || undefined,
                ctaTitle: ad.cta_title || undefined
            };
        }

        if (row.raw_data) {
            let raw = row.raw_data;
            if (typeof raw === 'string') {
                try { raw = JSON.parse(raw); } catch { raw = null; }
            }
            const sb = raw && typeof raw === 'object' ? (raw as any).sent_by : null;
            if (typeof raw?.client_message_id === 'string') message.clientMessageId = raw.client_message_id;
            const id = sb?.id != null ? String(sb.id).trim() : '';
            const full_name = sb?.full_name != null ? String(sb.full_name).trim() : '';
            if (id && full_name) {
                message.sentBy = { id, full_name };
            }
        }

        return message;
    }

    /**
     * Converte image_url que pode ser string, data URI, ou um Buffer serializado
     * como objeto numérico ({"0":255,"1":216,...}) para uma data URI válida.
     * 
     * Decisão: A Evolution API envia jpegThumbnail como Uint8Array/Buffer.
     * Quando o JSON.stringify é feito no backend sem conversão explícita,
     * o resultado é um objeto com chaves numéricas. Esta função recupera esses dados.
     */
    private static resolveImageUrl(imageUrl: unknown): string | undefined {
        if (!imageUrl) return undefined;

        // Caso 1: Já é uma string válida
        if (typeof imageUrl === 'string') {
            if (imageUrl.startsWith('data:') || imageUrl.startsWith('http')) {
                return imageUrl;
            }
            // Pode ser base64 puro
            if (imageUrl.length > 50) {
                return `data:image/jpeg;base64,${imageUrl}`;
            }
            return undefined;
        }

        // Caso 2: Objeto com chaves numéricas (Buffer serializado como JSON)
        // Ex: {"0":255,"1":216,"2":255,"3":224,...}
        if (typeof imageUrl === 'object' && imageUrl !== null && !Array.isArray(imageUrl)) {
            try {
                const keys = Object.keys(imageUrl as Record<string, number>);
                // Verifica se parece um Buffer: chaves numéricas consecutivas
                if (keys.length > 50 && keys[0] === '0' && keys[1] === '1') {
                    const obj = imageUrl as Record<string, number>;
                    const bytes = new Uint8Array(keys.length);
                    for (let i = 0; i < keys.length; i++) {
                        bytes[i] = obj[String(i)];
                    }
                    // Converte bytes para base64
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    const base64 = btoa(binary);
                    if (base64.length > 50) {
                        return `data:image/jpeg;base64,${base64}`;
                    }
                }
            } catch (e) {
                console.warn('[ChatMessageMapper] Falha ao converter Buffer serializado:', e);
            }
        }

        return undefined;
    }

    static toDatabase(message: ChatMessage, conversationDbId: string): any {
        return {
            conversation_id: conversationDbId,
            message_id: message.id,
            sender: message.sender,
            content: message.text || '',
            message_type: message.type,
            media_url: message.media?.url,
            status: message.status,
            timestamp: message.timestamp,
            quoted_message: message.quotedMessage ? JSON.stringify(message.quotedMessage) : null,
            is_edited: message.is_edited || false
        };
    }
}
